/**
 * useLLMEvidenceValidation Hook
 * ORCA360 - Tier 4 LLM Evidence Validation
 *
 * Calls /api/validate-evidence-llm for a single assessment.
 * Synchronous (no polling) — the endpoint returns updated evidence directly.
 *
 * Date: 2026-03-04
 */

import { useState, useCallback } from 'react';
import { Evidence } from '../components/LLMValidationPanel';

export interface LLMValidationStats {
  accepted: number;
  rejected: number;
  reclassified: number;
  total: number;
}

export interface LLMValidationParams {
  sessionId: string;
  assessmentId: string;
  assessmentType: 'pilot' | 'instructor';
  // Full evidence array to validate (current in-memory state)
  // For bulk validation, this should include all evidence items for the assessment.
  // For single-item modal validation, this can be a single-element array; in that case
  // the API will avoid overwriting the full evidence array in the database.
  evidence: any[];
}

export interface UseLLMEvidenceValidation {
  validate: (params: LLMValidationParams) => Promise<Evidence[]>;
  isValidating: boolean;
  error: string | null;
  stats: LLMValidationStats | null;
  reset: () => void;
}

export function useLLMEvidenceValidation(): UseLLMEvidenceValidation {
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<LLMValidationStats | null>(null);

  const reset = useCallback(() => {
    setIsValidating(false);
    setError(null);
    setStats(null);
  }, []);

  const validate = useCallback(async (params: LLMValidationParams): Promise<any[]> => {
    setIsValidating(true);
    setError(null);
    setStats(null);

    try {
      const response = await fetch('/api/validate-evidence-llm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `HTTP ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Validation failed');
      }

      setStats(data.stats ?? null);
      return data.updatedEvidence ?? [];
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      throw err;
    } finally {
      setIsValidating(false);
    }
  }, []);

  return { validate, isValidating, error, stats, reset };
}
